import { CompanyRole, FinancialReportPeriod, FinancialReportType } from '@lib/api-interface';
import { AddIcon, GeneralFormItem, GeneralFormRowOfItems, UsecaseButton } from '@lib/ui-components';
import React from 'react';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';

interface CreateReportButtonProps {
  reportType: FinancialReportType;
  onSaved: () => Promise<any>;
}

export const CreateSubcontractorReportButton = ({ reportType, onSaved }: CreateReportButtonProps) => {
  const { t } = useTranslation();
  const reportTypeSpecificFilters: (GeneralFormItem | GeneralFormRowOfItems)[] = [];

  // FinancialReportType.FINANCIAL_INCOME_AND_COSTS
  reportTypeSpecificFilters.push(
    {
      field: 'reportPeriod',
      label: t('reportPeriod'),
      type: 'select',
      selectOptions: Object.values(FinancialReportPeriod).map((val) => {
        return { label: t(`financialReportPeriod.${val}`), value: val };
      }),
    },
    {
      rowItems: [
        {
          field: 'start',
          label: t('month'),
          type: 'date-year-month',
        },
        {
          field: 'end',
          label: t('month'),
          type: 'date-year-month',
          isVisible: (data) => data.reportPeriod === FinancialReportPeriod.CUSTOM,
        },
        {
          field: 'sumMonths',
          label: t('sumMonths'),
          type: 'boolean',
          isVisible: (data) => data.reportPeriod === FinancialReportPeriod.CUSTOM,
        },
      ],
    },
    {
      rowItems: [
        {
          field: 'projectId',
          label: t('project'),
          type: 'table-select-by-api',
          tableSelectByApiParams: {
            dialogTitle: t('project'),
            displayFormat: '{name}',
            apiPostEndpoint: '/api/contractor/projects',
            selectedValueField: 'projectId',
            columns: [
              {
                label: t('name'),
                key: 'name',
                type: 'text',
              },
              {
                label: t('projectNumber'),
                key: 'projectNumber',
                type: 'text',
              },
            ],
          },
        },
      ],
    },
  );

  return (
    <UsecaseButton
      variant='contained'
      startIcon={<AddIcon />}
      onSaved={onSaved}
      groupName='reports'
      useCaseName='generate-report'
      buttonLabel={t('generateReport')}
      fields={[...reportTypeSpecificFilters]}
      onBeforeSave={(data) => {
        data.issuer = CompanyRole.SUBCONTRACTOR;
        return data;
      }}
      hiddenValues={{ reportType }}
      validationSchema={{
        reportPeriod: Yup.string().required(t('fieldRequired')),
        start: Yup.string().required(t('fieldRequired')),
        projectId: Yup.string().required(t('fieldRequired')),
      }}
    />
  );
};
